import {
  areReviewsLoading,
  getReviewsOpinions,
  getReviewsPageMeta,
  getPaginationError,
  isReviewsPaginationPageLoading,
  getReviewsCardsAndVariants,
  getReviewsPaginationWithVariants,
} from 'features/reviews/selectors';
import {
  PictureSources,
  ReviewData,
  ReviewsOpinionRecords,
  ReviewsPageMeta,
  ReviewsPaginationPage,
} from 'features/reviews/types';
import React from 'react';
import { connect } from 'react-redux';
import { RootReducer } from 'store/root-reducer';
import { Dispatch } from 'redux';
import {
  cancelDislikeForReview,
  cancelLikeForReview,
  changeDislikeToLikeForReview,
  changeLikeToDislikeForReview,
  FETCH_REVIEWS_NEXT_PAGE,
  setDislikeForReview,
  setLikeForReview,
  showPhoto,
} from 'features/reviews';
import { Breakpoint, ConnectedProps } from 'types/utils';
import { ReviewsPaginationFirstPage, ReviewsPaginationTail } from './content-blocks';

type ReviewsPaginationMappedState = {
  paginationTail: ReviewsPaginationPage[];
  firstPageCards: ReviewData[];
  firstPageMeta: ReviewsPageMeta | null;
  paginationPageIsLoading: boolean;
  paginationError: string | null;
  reviewsAreLoading: boolean;
  reviewsOpinions: ReviewsOpinionRecords[];
};
type ReviewsPaginationProps = {
  breakpoint: Breakpoint;
} & ReviewsPaginationMappedState &
  ConnectedProps<typeof mapDispatchToProps>;

export const ReviewsPagination: React.FC<ReviewsPaginationProps> = ({
  paginationTail,
  firstPageCards,
  firstPageMeta,
  paginationError,
  paginationPageIsLoading,
  reviewsAreLoading,
  breakpoint,
  reviewsOpinions,
  fetchNext,
  onShowPicture,
  onLike,
  onDislike,
  onCancelLike,
  onCancelDislike,
  onChangeDislikeToLike,
  onChangeLikeToDislike,
}) => {
  const showPaginationTail =
    !reviewsAreLoading && firstPageMeta !== null && firstPageMeta?.totalPages > firstPageMeta?.currentPage;
  return (
    <div data-qa="reviews-pagination_mf-pdp">
      <ReviewsPaginationFirstPage
        breakpoint={breakpoint}
        reviewsCards={firstPageCards}
        reviewsAreLoading={reviewsAreLoading}
        reviewsOpinions={reviewsOpinions}
        onShowPicture={onShowPicture}
        onLike={onLike}
        onDislike={onDislike}
        onCancelLike={onCancelLike}
        onCancelDislike={onCancelDislike}
        onChangeDislikeToLike={onChangeDislikeToLike}
        onChangeLikeToDislike={onChangeLikeToDislike}
      />

      {showPaginationTail && (
        <ReviewsPaginationTail
          breakpoint={breakpoint}
          paginationPageIsLoading={paginationPageIsLoading}
          paginationTail={paginationTail}
          reviewsOpinions={reviewsOpinions}
          fetchNext={fetchNext}
          onShowPicture={onShowPicture}
          onLike={onLike}
          onDislike={onDislike}
          onCancelLike={onCancelLike}
          onCancelDislike={onCancelDislike}
          onChangeDislikeToLike={onChangeDislikeToLike}
          onChangeLikeToDislike={onChangeLikeToDislike}
          paginationError={paginationError}
        />
      )}
    </div>
  );
};

export const mapStoreToProps = (store: RootReducer): ReviewsPaginationMappedState => {
  const paginationTail = getReviewsPaginationWithVariants(store);
  const firstPageCards = getReviewsCardsAndVariants(store);
  const firstPageMeta = getReviewsPageMeta(store);
  const paginationPageIsLoading = isReviewsPaginationPageLoading(store);
  const reviewsAreLoading = areReviewsLoading(store);
  const reviewsOpinions = getReviewsOpinions(store);
  const paginationError = getPaginationError(store);

  return {
    paginationError,
    paginationTail,
    firstPageCards,
    firstPageMeta,
    paginationPageIsLoading,
    reviewsAreLoading,
    reviewsOpinions,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchNext: (currentPage: number) => dispatch(FETCH_REVIEWS_NEXT_PAGE.request({ currentPage })),
  onShowPicture: (pic: PictureSources | null) => dispatch(showPhoto(pic)),
  onLike: (reviewId: string) => dispatch(setLikeForReview(reviewId)),
  onDislike: (reviewId: string) => dispatch(setDislikeForReview(reviewId)),
  onCancelLike: (reviewId: string) => dispatch(cancelLikeForReview(reviewId)),
  onCancelDislike: (reviewId: string) => dispatch(cancelDislikeForReview(reviewId)),
  onChangeDislikeToLike: (reviewId: string) => dispatch(changeDislikeToLikeForReview(reviewId)),
  onChangeLikeToDislike: (reviewId: string) => dispatch(changeLikeToDislikeForReview(reviewId)),
});

export const PdpReviewsPagination = connect(mapStoreToProps, mapDispatchToProps)(ReviewsPagination);
